import { useState } from "react";
import { Link, useNavigate } from "react-router";

import { Badge } from "@/components/StatusBadge";
import { DropdownMenu } from "@/components/ui/DropdownMenu";
import { MoreIcon } from "@/components/ui/icons";
import type { RenewalResource } from "@/lib/api-types";
import { renewalBoardMenuItems } from "@/lib/enum-labels";
import { formatDateCell, formatMoneyCell } from "@/lib/format";
import { formatRentChangePercent, monthlyRentChangeCents, rentChangePercent } from "@/lib/rent-change";

/**
 * One renewal board card (Story 11.3, DESIGN.md § components.board-card):
 * `{colors.surface}`, a hairline border, `{rounded.md}`, `{spacing.board-
 * card-min}` minimum — holding tenant/property identity, the lease end
 * date, current rent and (when set) the proposed rent with its $/% change,
 * plus the tap-to-move menu and the stall badge.
 *
 * Tapping/clicking the card navigates to `/renewals/:id` (AC) — mirrors
 * `DataTable.tsx`'s own identifier-cell shape exactly: the whole card gets
 * a synthetic `onClick={() => navigate(...)}` (a real tap target across its
 * full surface, not just one line of text — DESIGN.md's own "the board must
 * work one-handed on a phone" concern), plus one real, nested `<Link>`
 * around the tenant/property identity for keyboard/right-click/"open in new
 * tab" to work (the same `<tr>`+`<Link>` pairing `DataTable.tsx`'s own doc
 * comment explains was a real, found-live accessibility gap the first time
 * around). The tap-to-move trigger stops its own click from also bubbling
 * into the card's `onClick`, mirroring `DataTable.tsx`'s identical
 * row-action `stopPropagation` precedent.
 */
export function RenewalBoardCard({
  renewal,
  isOwner,
  transitioning,
  onTransition,
}: {
  renewal: RenewalResource;
  isOwner: boolean;
  transitioning: boolean;
  onTransition: (renewalId: string, event: string) => void;
}) {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuItems = renewalBoardMenuItems(renewal.status);
  const recordHref = `/renewals/${renewal.id}`;
  const tenantLabel = renewal.tenant_names.length > 0 ? renewal.tenant_names.join(" & ") : "No tenant on file";

  // `current_rent_cents` resolves to `null` only when the source lease
  // itself cannot be found at all (`RenewalResource`'s own doc comment) —
  // a genuinely rare, already-degraded state; the $/% change simply has
  // nothing real to compare the proposal against, so it renders neither.
  const hasProposal = renewal.proposed_rent_cents !== null && renewal.current_rent_cents !== null;
  const monthlyChangeCents = hasProposal
    ? monthlyRentChangeCents(renewal.current_rent_cents as number, renewal.proposed_rent_cents as number)
    : null;
  const changePercent = hasProposal
    ? rentChangePercent(renewal.current_rent_cents as number, renewal.proposed_rent_cents as number)
    : null;

  return (
    <div
      onClick={() => navigate(recordHref)}
      className="min-h-board-card-min cursor-pointer rounded-md border border-border-hairline bg-surface p-3 hover:bg-surface-hover"
    >
      <div className="flex items-start justify-between gap-2">
        <Link to={recordHref} className="min-w-0 flex-1">
          <p className="truncate text-body font-medium text-ink">{tenantLabel}</p>
          <p className="mt-0.5 truncate text-meta text-ink-secondary">
            {renewal.property_nickname} · {renewal.unit_label}
          </p>
        </Link>
        {isOwner && menuItems.length > 0 ? (
          // `stopPropagation` lives on this wrapper, not only on the
          // trigger button: Radix's `Portal` renders the open menu's
          // content elsewhere in the DOM, but React's synthetic events
          // still bubble along the *React* tree, not the DOM tree — a
          // click on a menu item is still a descendant of `DropdownMenu`
          // here and would otherwise reach the card's own `onClick` above
          // and navigate away the instant an event fires (found while
          // building this, not a restatement of `DataTable.tsx`'s simpler,
          // non-portaled row-action precedent).
          <div onClick={(event) => event.stopPropagation()}>
            <DropdownMenu
              open={menuOpen}
              onOpenChange={setMenuOpen}
              align="end"
              trigger={
                <button
                  type="button"
                  aria-label="Move to the next stage"
                  disabled={transitioning}
                  className="inline-flex h-tap-target-min w-tap-target-min shrink-0 items-center justify-center rounded-md text-ink-secondary hover:bg-surface-hover disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <MoreIcon />
                </button>
              }
              items={menuItems.map((item) => ({
                label: item.label,
                onSelect: () => onTransition(renewal.id, item.event),
              }))}
            />
          </div>
        ) : null}
      </div>

      {renewal.stalled ? (
        <div className="mt-2">
          <Badge label="Stalled" fill="attention" />
        </div>
      ) : null}

      <div className="mt-3 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 text-meta text-ink-secondary">
        <span>
          Lease ends{" "}
          <span className="numeric-features text-ink">
            {renewal.lease_end_date ? formatDateCell(renewal.lease_end_date) : "—"}
          </span>
        </span>
        <span className="numeric-features text-money">{formatMoneyCell(renewal.current_rent_cents)}</span>
      </div>

      {renewal.proposed_rent_cents !== null ? (
        <div className="mt-1 flex items-baseline justify-between gap-2 text-meta">
          <span className="text-ink-secondary">Proposed</span>
          <span className="numeric-features text-money">
            <span>{formatMoneyCell(renewal.proposed_rent_cents)}</span>
            {monthlyChangeCents !== null ? (
              <span className="ml-2 text-ink-secondary">
                {(monthlyChangeCents >= 0 ? "+" : "") +
                  formatMoneyCell(monthlyChangeCents) +
                  (changePercent !== null ? ` (${formatRentChangePercent(changePercent)})` : "")}
              </span>
            ) : null}
          </span>
        </div>
      ) : null}
    </div>
  );
}
